import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";

import { DEFAULT_PAGE_SIZE } from "@/config";

import {
  AppPlan,
  AppPlanService,
  AppPlanFilter,
  Pagination,
  listPage,
} from "@/utils/api";

import { SortConfig } from "@/components/generic/AppTable";

export interface UseAppPlansOptions {
  /** Space ID to filter app plans by */
  spaceId?: string | undefined;

  /** Initial page size for pagination */
  pageSize?: number;

  /** Initial filters */
  initialFilters?: Partial<AppPlanFilter>;

  /** Initial sort configuration */
  initialSort?: SortConfig[];

  /** Auto-load data on mount */
  autoLoad?: boolean;
}

export interface UseAppPlansReturn {
  // Data state
  appPlan: AppPlan | null;
  appPlans: AppPlan[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<AppPlanFilter>;
  sortBy: SortConfig[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<void>;
  list: (
    filter?: Partial<AppPlanFilter>,
    pagination?: Pagination,
    sortBy?: SortConfig[],
    reset?: boolean
  ) => Promise<void>;
  getMany: (ids: string[]) => Promise<AppPlan[]>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;

  // Filter operations
  setFilters: (filters: Partial<AppPlanFilter>) => void;
  setSortBy: (sort: SortConfig[]) => void;

  // Helper operations
  reset: () => void;

  // Computed values
  featuredPlans: AppPlan[];

  // Error handling
  lastError: string | null;
  clearError: () => void;
}

const DEFAULT_SORT: SortConfig[] = [
  { key: "app_plans.order_no", direction: "asc" },
];

export const useAppPlans = (
  options: UseAppPlansOptions = {}
): UseAppPlansReturn => {
  const {
    spaceId,
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = {},
    initialSort = DEFAULT_SORT,
    autoLoad = true,
  } = options;

  // Use refs for option values to avoid dependency issues
  const spaceIdRef = useRef(spaceId);
  const pageSizeRef = useRef(pageSize);
  const sortByRef = useRef<SortConfig[]>(initialSort);
  // Bumped by every list read: a page that lands after a newer read began is not this list's (#119).
  const listCallIdRef = useRef(0);

  // Update refs when options change
  useEffect(() => {
    spaceIdRef.current = spaceId;
  }, [spaceId]);

  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  // Data state
  const [appPlan, setAppPlan] = useState<AppPlan | null>(null);
  const [appPlans, setAppPlans] = useState<AppPlan[]>([]);
  /**
   * ⚠⚠ STARTS TRUE WHEN THIS HOOK IS GOING TO AUTO-LOAD — "about to read" is not "read, and empty".
   *
   * The auto-load effect is gated on `userId`, which arrives ASYNCHRONOUSLY from `AppContext`. So on
   * a hard reload or a first navigation there are renders where no request has started yet: with
   * `loading` false and the collection empty, every list component's
   * `!loading && items.length === 0` branch is TRUE and paints the first-run empty state — "No notes
   * yet", "No tasks yet" — before the skeleton and then the data. Reported across the whole event
   * space (owner, 2026-09-28).
   * ⓘ Fixed HERE rather than in the ~45 list components that read it: the lie is in the flag, not in
   * the branch, and a per-component fix would have to be remembered by every list written after it.
   * ⚠ `autoLoad: false` still starts FALSE — a hook told not to load must not claim to be loading.
   */
  const [loading, setLoading] = useState(autoLoad);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] = useState<Partial<AppPlanFilter>>(() => {
    const filter = { ...initialFilters };
    return filter;
  });
  const [sortBy, setSortBy] = useState<SortConfig[]>(initialSort);

  // Update sortByRef when sortBy state changes
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Error handling
  const [lastError, setLastError] = useState<string | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => appPlans.length === 0 && !loading,
    [appPlans.length, loading]
  );

  const featuredPlans = useMemo(() => {
    return appPlans.filter((plan) => plan.isPlanFeatured === true);
  }, [appPlans]);

  // Helper function to update app plan in list if it exists, or add it if not
  const updateOrAddAppPlanInList = useCallback((updatedAppPlan: AppPlan) => {
    setAppPlans((prev) => {
      const existingIndex = prev.findIndex((p) => p.id === updatedAppPlan.id);
      if (existingIndex >= 0) {
        const newAppPlans = [...prev];
        newAppPlans[existingIndex] = updatedAppPlan;
        return newAppPlans;
      } else {
        return [updatedAppPlan, ...prev];
      }
    });
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    listCallIdRef.current++;
    setAppPlan(null);
    setAppPlans([]);
    setLastError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
  }, []);

  // Get single app plan (matching API method name)
  const get = useCallback(
    async (id: string) => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await AppPlanService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const appPlanData = result.data[0] as AppPlan;
          setAppPlan(appPlanData);
          updateOrAddAppPlanInList(appPlanData);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load app plan";
          setLastError(errorMessage);
          setAppPlan(null);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load app plan";
        setLastError(errorMessage);
        setAppPlan(null);
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddAppPlanInList]
  );

  // List app plans (matching API method name) - NO dependencies
  const list = useCallback(
    async (
      filter: Partial<AppPlanFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortConfig[],
      reset: boolean = false
    ) => {
      const callId = ++listCallIdRef.current;
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !reset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        // A reload owns the list: a Load more it supersedes is dropped, and so is its spinner (#114).
        setLoadingMore(false);
        if (reset) {
          setAppPlans([]);
          setCurrentPage(1);
        }
      }

      const combinedFilter = { ...filter };

      setFilters(combinedFilter);
      if (sortByParams) setSortBy(sortByParams);

      const currentSortBy = sortByParams || sortByRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => ({
          name: sort.key,
          order: sort.direction.toUpperCase() as "ASC" | "DESC",
        }));

        const result = await listPage(
          AppPlanService.list,
          combinedFilter as AppPlanFilter,
          paginationParams,
          sortParams
        );

        // A newer read began while this one was out (a refresh, another filter): its rows are not this list's (#119).
        if (callId !== listCallIdRef.current) {
          return;
        }

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newAppPlans: AppPlan[] = Array.isArray(result.data)
            ? result.data
            : [];

          if (isAppending) {
            setAppPlans((prev) => {
              const existingIds = new Set(prev.map((p) => p.id));
              const uniqueNewAppPlans = newAppPlans.filter(
                (p) => !existingIds.has(p.id)
              );
              return [...prev, ...uniqueNewAppPlans];
            });
          } else {
            setAppPlans(newAppPlans);
          }

          setHasMore(newAppPlans.length === targetPageSize);
          setCurrentPage(targetPage);
          setLastError(null);
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load app plans";
          setLastError(errorMessage);
          if (!isAppending) setAppPlans([]);
        }
      } catch (error) {
        if (callId !== listCallIdRef.current) {
          return;
        }

        const errorMessage =
          error instanceof Error ? error.message : "Failed to load app plans";
        setLastError(errorMessage);
        if (!isAppending) setAppPlans([]);
      } finally {
        // ⚠ Only the current read settles the flags: a stale one would clear a NEWER read's spinner.
        if (callId === listCallIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    []
  );

  // Get many app plans by IDs (matching API method name)
  const getMany = useCallback(
    async (ids: string[]): Promise<AppPlan[]> => {
      if (ids.length === 0) {
        return [];
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await AppPlanService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedAppPlans: AppPlan[] = Array.isArray(result.data)
            ? result.data
            : [];
          loadedAppPlans.forEach((plan) => {
            updateOrAddAppPlanInList(plan);
          });
          setLastError(null);
          return loadedAppPlans;
        } else {
          const errorMessage =
            result.errors?.[0]?.errors?.[0] || "Failed to load app plans";
          setLastError(errorMessage);
          return [];
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Failed to load app plans";
        setLastError(errorMessage);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddAppPlanInList]
  );

  // Load more app plans
  const loadMore = useCallback(async () => {
    if (!loading && !loadingMore && hasMore) {
      const nextPage = currentPage + 1;
      await list(
        filters,
        { page: nextPage, pageSize: pageSizeRef.current },
        sortByRef.current,
        false
      );
    }
  }, [list, loading, loadingMore, hasMore, currentPage, filters]);

  // Refresh app plans
  const refresh = useCallback(async () => {
    await list(
      filters,
      { page: 1, pageSize: pageSizeRef.current },
      sortByRef.current,
      true
    );
  }, [list, filters]);

  // Auto-load effect
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);

  useEffect(() => {
    if (autoLoad && !hasAutoLoaded) {
      setHasAutoLoaded(true);
      const autoLoadFilters = { ...initialFilters };

      list(autoLoadFilters, { page: 1, pageSize }, initialSort, true);
    }
  }, [autoLoad, hasAutoLoaded, list, pageSize, initialSort, initialFilters]);

  return {
    appPlan,
    appPlans,
    loading,
    loadingMore,
    hasMore,
    isEmpty,
    filters,
    sortBy,
    currentPage,
    pageSize,
    get,
    list,
    getMany,
    loadMore,
    refresh,
    setFilters,
    setSortBy,
    reset,
    featuredPlans,
    lastError,
    clearError,
  };
};
